/**
 * usePersona — 페르소나 상태 Hook (Wave 4B)
 *
 * localStorage 기반 페르소나 지속성 + React 상태 동기화
 */

import { useState, useCallback, useEffect } from "react";
import type {
  PersonaId,
  PersonaDefinition,
} from "@modootoday/core-agent/persona";
import {
  getCurrentPersona,
  setCurrentPersona,
  clearPersona,
  getPersona,
} from "@modootoday/core-agent/persona";

export interface UsePersonaReturn {
  /** 현재 선택된 페르소나 정의 (null: 미설정) */
  persona: PersonaDefinition | null;
  /** 현재 페르소나 ID (null: 미설정) */
  personaId: PersonaId | null;
  /** 페르소나 설정 */
  setPersona: (id: PersonaId) => void;
  /** 페르소나 초기화 */
  clearPersona: () => void;
  /** localStorage 초기 로드 완료 여부 */
  isLoaded: boolean;
}

export function usePersona(): UsePersonaReturn {
  const [personaId, setPersonaId] = useState<PersonaId | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // 첫 마운트 시 localStorage에서 로드
  useEffect(() => {
    const stored = getCurrentPersona();
    if (stored) {
      setPersonaId(stored);
    }
    setIsLoaded(true);
  }, []);

  const setPersonaCallback = useCallback((id: PersonaId) => {
    setCurrentPersona(id);
    setPersonaId(id);
  }, []);

  const clearPersonaCallback = useCallback(() => {
    clearPersona();
    setPersonaId(null);
  }, []);

  const persona = personaId ? getPersona(personaId) : null;

  return {
    persona,
    personaId,
    setPersona: setPersonaCallback,
    clearPersona: clearPersonaCallback,
    isLoaded,
  };
}
